import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';

/**
 * Frontend client for the GIS-based Asset Management module.
 *
 * Backend mount point: `/api/asset-management`.
 * Auth uses the existing `Authorization: Bearer <token>` header populated
 * via the global HTTP interceptor; no extra headers needed here.
 */

export type AssetClassification = 'underground' | 'overground' | 'crossover';

export type AssetStatus =
  | 'planned'
  | 'under_construction'
  | 'active'
  | 'inactive'
  | 'decommissioned'
  | 'abandoned';

export type AssetGeometryType =
  | 'POINT'
  | 'LINESTRING'
  | 'POLYGON'
  | 'MULTIPOINT'
  | 'MULTILINESTRING'
  | 'MULTIPOLYGON';

export interface AssetLayer {
  id: number;
  organization_id: number | null;
  branch_id: number | null;
  code: string;
  name: string;
  description?: string | null;
  classification: AssetClassification;
  asset_category: string;
  geometry_type: AssetGeometryType;
  srid: number;
  default_style?: Record<string, unknown>;
  attribute_schema?: Record<string, unknown>;
  permissions?: Record<string, unknown>;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface AssetGeoJSONGeometry {
  type:
    | 'Point'
    | 'LineString'
    | 'Polygon'
    | 'MultiPoint'
    | 'MultiLineString'
    | 'MultiPolygon';
  coordinates: number[] | number[][] | number[][][] | number[][][][];
}

export interface AssetFeatureProperties {
  uuid?: string;
  layer_id: number;
  organization_id: number | null;
  branch_id: number | null;
  asset_code?: string | null;
  name?: string | null;
  description?: string | null;
  classification: AssetClassification;
  asset_category: string;
  status: AssetStatus;
  depth_m?: number | null;
  elevation_m?: number | null;
  attributes?: Record<string, unknown>;
  current_version?: number;
  is_deleted?: boolean;
  installed_on?: string | null;
  decommissioned_on?: string | null;
  metadata?: Record<string, unknown>;
  centroid?: [number, number];
  topology_warnings?: string[];
  trace_depth?: number;
  trace_path?: number[];
  distance_m?: number;
  created_at?: string;
  updated_at?: string;
  geom_type?: string;
}

export interface AssetFeature {
  type: 'Feature';
  id: number;
  geometry: AssetGeoJSONGeometry;
  properties: AssetFeatureProperties;
}

export interface AssetFeatureCollection {
  type: 'FeatureCollection';
  features: AssetFeature[];
  count?: number;
  limit?: number;
  offset?: number;
  distance_m?: number;
  start_asset_id?: number;
  direction?: string;
  max_depth?: number;
}

export interface AssetVersion {
  id: number;
  asset_id: number;
  version: number;
  status: AssetStatus;
  depth_m: number | null;
  elevation_m: number | null;
  change_type: string;
  change_summary: string | null;
  changed_by: number | null;
  changed_by_type: number | null;
  changed_at: string;
  attributes: Record<string, unknown>;
  geometry: AssetGeoJSONGeometry | null;
}

export interface AuditEntry {
  id: number;
  organization_id: number | null;
  branch_id: number | null;
  asset_id: number | null;
  layer_id: number | null;
  action: string;
  entity: string;
  user_id: number | null;
  user_type: number | null;
  ip_address: string | null;
  detail: string | null;
  created_at: string;
}

export interface DashboardStats {
  total_assets: number;
  layers: number;
  relationships: number;
  versions: number;
  by_classification: Record<string, number>;
  by_status: Record<string, number>;
  by_category: Array<{ category: string; count: number }>;
}

export interface ErpIntegration {
  id: number;
  organization_id: number | null;
  branch_id: number | null;
  provider: string;
  name: string;
  instance_key: string | null;
  auth_config?: Record<string, unknown>;
  mapping_config?: Record<string, unknown>;
  schedule_config?: Record<string, unknown>;
  is_enabled: boolean;
  last_sync_at?: string | null;
  created_at: string;
  updated_at: string;
}

export interface ErpSyncRun {
  id: number;
  integration_id: number | null;
  provider: string | null;
  mode: string;
  status: string;
  dry_run: boolean;
  total_records: number;
  created_count: number;
  updated_count: number;
  skipped_count: number;
  failed_count: number;
  started_at: string;
  finished_at: string | null;
}

export interface AssetSearchParams {
  layerId?: number;
  classification?: AssetClassification;
  assetCategory?: string;
  status?: AssetStatus;
  search?: string;
  bbox?: string;
  nearLng?: number;
  nearLat?: number;
  nearRadiusM?: number;
  includeDeleted?: boolean;
  limit?: number;
  offset?: number;
}

export interface CreateLayerRequest {
  code: string;
  name: string;
  description?: string;
  classification: AssetClassification;
  asset_category: string;
  geometry_type: AssetGeometryType;
  srid?: number;
  default_style?: Record<string, unknown>;
  attribute_schema?: Record<string, unknown>;
  permissions?: Record<string, unknown>;
  is_active?: boolean;
}

export interface CreateAssetRequest {
  layer_id: number;
  asset_code?: string;
  name?: string;
  description?: string;
  classification: AssetClassification;
  asset_category?: string;
  status?: AssetStatus;
  geometry: AssetGeoJSONGeometry;
  srid?: number;
  depth_m?: number | null;
  elevation_m?: number | null;
  attributes?: Record<string, unknown>;
  installed_on?: string;
  decommissioned_on?: string;
  metadata?: Record<string, unknown>;
  topology_tolerance_m?: number;
}

export type UpdateAssetRequest = Partial<CreateAssetRequest> & {
  change_summary?: string;
};

export interface RelationshipRequest {
  from_asset_id: number;
  to_asset_id: number;
  relationship_type:
    | 'connects_to'
    | 'contains'
    | 'adjacent_to'
    | 'feeds'
    | 'crosses'
    | 'parallel_to';
  directional?: boolean;
  weight?: number;
  metadata?: Record<string, unknown>;
}

export interface SaveErpIntegrationRequest {
  provider: string;
  name: string;
  instance_key?: string;
  auth_config?: Record<string, unknown>;
  mapping_config?: Record<string, unknown>;
  schedule_config?: Record<string, unknown>;
  is_enabled?: boolean;
}

export interface ErpSyncRecord {
  external_id: string;
  layer_id?: number;
  layer_code?: string;
  asset_code?: string;
  name?: string;
  description?: string;
  classification?: AssetClassification;
  asset_category?: string;
  status?: AssetStatus;
  lng?: number;
  lat?: number;
  depth_m?: number | null;
  elevation_m?: number | null;
  attributes?: Record<string, unknown>;
  metadata?: Record<string, unknown>;
}

@Injectable({ providedIn: 'root' })
export class AssetManagementService {
  private readonly base = `${environment.BASE_URL}asset-management`;

  constructor(private http: HttpClient) {}

  private get headers(): { headers: HttpHeaders } {
    return {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
    };
  }

  health(): Observable<{ ok: boolean; postgis: boolean; message: string }> {
    return this.http.get<{ ok: boolean; postgis: boolean; message: string }>(
      `${this.base}/health`,
      this.headers
    );
  }

  dashboard(): Observable<{ data: DashboardStats }> {
    return this.http.get<{ data: DashboardStats }>(
      `${this.base}/dashboard`,
      this.headers
    );
  }

  // ----- Layers -----

  listLayers(includeInactive = false): Observable<{ data: AssetLayer[] }> {
    const params = new HttpParams().set(
      'includeInactive',
      includeInactive ? 'true' : 'false'
    );
    return this.http.get<{ data: AssetLayer[] }>(`${this.base}/layers`, {
      ...this.headers,
      params,
    });
  }

  createLayer(body: CreateLayerRequest): Observable<{ data: AssetLayer }> {
    return this.http.post<{ data: AssetLayer }>(
      `${this.base}/layers`,
      body,
      this.headers
    );
  }

  updateLayer(
    id: number,
    body: Partial<CreateLayerRequest>
  ): Observable<{ data: AssetLayer }> {
    return this.http.put<{ data: AssetLayer }>(
      `${this.base}/layers/${id}`,
      body,
      this.headers
    );
  }

  deleteLayer(id: number): Observable<{ data: { id: number; is_active: boolean } }> {
    return this.http.delete<{ data: { id: number; is_active: boolean } }>(
      `${this.base}/layers/${id}`,
      this.headers
    );
  }

  // ----- Assets -----

  searchAssets(filters: AssetSearchParams = {}): Observable<AssetFeatureCollection> {
    let params = new HttpParams();
    Object.entries(filters).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') {
        params = params.set(k, String(v));
      }
    });
    return this.http.get<AssetFeatureCollection>(`${this.base}/assets`, {
      ...this.headers,
      params,
    });
  }

  getAsset(id: number): Observable<AssetFeature> {
    return this.http.get<AssetFeature>(`${this.base}/assets/${id}`, this.headers);
  }

  createAsset(body: CreateAssetRequest): Observable<AssetFeature> {
    return this.http.post<AssetFeature>(
      `${this.base}/assets`,
      body,
      this.headers
    );
  }

  updateAsset(id: number, body: UpdateAssetRequest): Observable<AssetFeature> {
    return this.http.put<AssetFeature>(
      `${this.base}/assets/${id}`,
      body,
      this.headers
    );
  }

  deleteAsset(id: number): Observable<{ id: number; is_deleted: boolean }> {
    return this.http.delete<{ id: number; is_deleted: boolean }>(
      `${this.base}/assets/${id}`,
      this.headers
    );
  }

  restoreAsset(id: number): Observable<AssetFeature> {
    return this.http.post<AssetFeature>(
      `${this.base}/assets/${id}/restore`,
      {},
      this.headers
    );
  }

  // ----- Versions / audit -----

  listVersions(
    id: number,
    limit = 50,
    offset = 0
  ): Observable<{ data: AssetVersion[] }> {
    const params = new HttpParams()
      .set('limit', String(limit))
      .set('offset', String(offset));
    return this.http.get<{ data: AssetVersion[] }>(
      `${this.base}/assets/${id}/versions`,
      { ...this.headers, params }
    );
  }

  rollbackVersion(id: number, version: number): Observable<AssetFeature> {
    return this.http.post<AssetFeature>(
      `${this.base}/assets/${id}/versions/${version}/rollback`,
      {},
      this.headers
    );
  }

  listAudit(
    options: { assetId?: number; sinceMs?: number; limit?: number } = {}
  ): Observable<{ data: AuditEntry[]; server_time: number }> {
    let params = new HttpParams();
    if (options.assetId) params = params.set('assetId', String(options.assetId));
    if (options.sinceMs) params = params.set('sinceMs', String(options.sinceMs));
    if (options.limit) params = params.set('limit', String(options.limit));
    return this.http.get<{ data: AuditEntry[]; server_time: number }>(
      `${this.base}/audit`,
      { ...this.headers, params }
    );
  }

  // ----- Relationships -----

  listRelationships(assetId: number): Observable<{ data: any[] }> {
    return this.http.get<{ data: any[] }>(
      `${this.base}/assets/${assetId}/relationships`,
      this.headers
    );
  }

  addRelationship(body: RelationshipRequest): Observable<{ data: any }> {
    return this.http.post<{ data: any }>(
      `${this.base}/relationships`,
      body,
      this.headers
    );
  }

  removeRelationship(id: number): Observable<{ data: any }> {
    return this.http.delete<{ data: any }>(
      `${this.base}/relationships/${id}`,
      this.headers
    );
  }

  // ----- Spatial analysis -----

  buffer(geometry: AssetGeoJSONGeometry, distanceM: number, limit?: number) {
    return this.http.post<AssetFeatureCollection>(
      `${this.base}/spatial/buffer`,
      { geometry, distance_m: distanceM, limit },
      this.headers
    );
  }

  geofence(fence: AssetGeoJSONGeometry, layerId?: number) {
    return this.http.post<AssetFeatureCollection>(
      `${this.base}/spatial/geofence`,
      { fence, layer_id: layerId },
      this.headers
    );
  }

  networkTrace(opts: {
    startAssetId: number;
    direction?: 'downstream' | 'upstream' | 'both';
    maxDepth?: number;
    relationshipTypes?: string[];
  }) {
    return this.http.post<AssetFeatureCollection>(
      `${this.base}/spatial/network-trace`,
      {
        start_asset_id: opts.startAssetId,
        direction: opts.direction,
        max_depth: opts.maxDepth,
        relationship_types: opts.relationshipTypes,
      },
      this.headers
    );
  }

  impact(assetId: number, maxDepth?: number) {
    let params = new HttpParams();
    if (maxDepth) params = params.set('maxDepth', String(maxDepth));
    return this.http.get<{
      start_asset_id: number;
      impacted_count: number;
      by_category: Record<string, number>;
      features: AssetFeature[];
    }>(`${this.base}/spatial/impact/${assetId}`, { ...this.headers, params });
  }

  // ----- Import / export -----

  exportLayer(format: 'geojson' | 'kml', layerId?: number): Observable<Blob> {
    let params = new HttpParams().set('format', format);
    if (layerId) params = params.set('layerId', String(layerId));
    return this.http.get(`${this.base}/export`, {
      params,
      responseType: 'blob',
    });
  }

  importGeoJSON(layerId: number, payload: any) {
    return this.http.post<{
      success: boolean;
      importId?: number;
      total: number;
      succeeded: number;
      failed: number;
      errors: Array<{ message: string; name: string | null }>;
    }>(
      `${this.base}/import`,
      { layer_id: layerId, format: 'geojson', payload },
      this.headers
    );
  }

  importKML(layerId: number, kmlString: string) {
    return this.http.post<{
      success: boolean;
      importId?: number;
      total: number;
      succeeded: number;
      failed: number;
    }>(
      `${this.base}/import`,
      { layer_id: layerId, format: 'kml', payload: kmlString },
      this.headers
    );
  }

  // ----- ERP integration -----

  listErpIntegrations(): Observable<{ data: ErpIntegration[] }> {
    return this.http.get<{ data: ErpIntegration[] }>(
      `${this.base}/erp/integrations`,
      this.headers
    );
  }

  saveErpIntegration(
    body: SaveErpIntegrationRequest
  ): Observable<{ data: ErpIntegration }> {
    return this.http.post<{ data: ErpIntegration }>(
      `${this.base}/erp/integrations`,
      body,
      this.headers
    );
  }

  runErpSync(body: {
    integration_id?: number;
    provider?: string;
    dry_run?: boolean;
    mode?: 'manual' | 'scheduled';
    records: ErpSyncRecord[];
  }): Observable<{
    data: {
      run_id: number;
      status: string;
      total: number;
      created: number;
      updated: number;
      skipped: number;
      failed: number;
      dry_run: boolean;
      errors: Array<{ index: number; message: string }>;
    };
  }> {
    return this.http.post<{
      data: {
        run_id: number;
        status: string;
        total: number;
        created: number;
        updated: number;
        skipped: number;
        failed: number;
        dry_run: boolean;
        errors: Array<{ index: number; message: string }>;
      };
    }>(`${this.base}/erp/sync`, body, this.headers);
  }

  listErpSyncRuns(
    options: { integrationId?: number; limit?: number } = {}
  ): Observable<{ data: ErpSyncRun[] }> {
    let params = new HttpParams();
    if (options.integrationId) params = params.set('integrationId', String(options.integrationId));
    if (options.limit) params = params.set('limit', String(options.limit));
    return this.http.get<{ data: ErpSyncRun[] }>(`${this.base}/erp/sync-runs`, {
      ...this.headers,
      params,
    });
  }

  seedDemo(replace = true): Observable<{
    success: boolean;
    data: {
      layers: number;
      assets_created: number;
      assets_skipped: number;
      corridor: string;
      codes: string[];
    };
    message?: string;
  }> {
    return this.http.post<{
      success: boolean;
      data: {
        layers: number;
        assets_created: number;
        assets_skipped: number;
        corridor: string;
        codes: string[];
      };
      message?: string;
    }>(`${this.base}/demo/seed`, { replace }, this.headers);
  }
}
